import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { invoicesApi } from "@/services/api";
import { useAsync } from "@/hooks/useAsync";
import { fmtMoney, fmtDate, statusTone, prettyStatus } from "@/lib/format";
import PageHeader from "@/components/PageHeader";
import { LoadingState, ErrorState } from "@/components/DataStates";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

export default function PortalInvoiceDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: inv, loading, error, reload } = useAsync(() => invoicesApi.getOne(id), [id]);

  if (loading) return <div className="mx-auto max-w-3xl"><Back nav={navigate} /><LoadingState label="Loading invoice…" /></div>;
  if (error) return <div className="mx-auto max-w-3xl"><Back nav={navigate} /><ErrorState message={error} onRetry={reload} /></div>;
  if (!inv) return null;

  const paid = inv.status === "paid";
  const items = inv.items_snapshot || inv.items || [];

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <Back nav={navigate} />
      <PageHeader
        title={inv.invoice_number}
        subtitle={inv.building_name || ""}
        actions={<Badge variant="outline" className={statusTone(inv.overdue && !paid ? "overdue" : inv.status)}>{inv.overdue && !paid ? `Overdue ${inv.days_overdue}d` : prettyStatus(inv.status)}</Badge>}
      />

      {paid && (
        <Card className="flex items-center gap-2 border-emerald-100 bg-emerald-50 p-4 text-sm font-semibold text-emerald-700">
          <CheckCircle2 className="h-5 w-5" /> Paid{inv.paid_at ? ` on ${fmtDate(inv.paid_at)}` : ""}
        </Card>
      )}

      <Card className="grid grid-cols-2 gap-4 p-4 text-sm">
        <Field label="Issued" value={fmtDate(inv.invoice_date)} />
        <Field label="Due" value={fmtDate(inv.due_date)} />
        <Field label="Payment terms" value={inv.payment_terms_days != null ? `${inv.payment_terms_days} days` : "—"} />
        <Field label="Type" value={prettyStatus(inv.invoice_kind || "full")} />
      </Card>

      {items.length > 0 && (
        <Card className="overflow-hidden">
          <div className="border-b border-slate-100 bg-slate-50 px-4 py-2.5 text-sm font-semibold text-slate-700">Items</div>
          <div className="divide-y divide-slate-50">
            {items.map((it, i) => (
              <div key={i} className="flex items-center justify-between gap-3 px-4 py-3 text-sm">
                <span className="min-w-0 truncate text-slate-700">{it.component || it.description || "Item"}</span>
                <span className="shrink-0 font-semibold text-slate-800">{fmtMoney(Number(it.cost || 0) * Number(it.quantity || 1))}</span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <Card className="p-4">
        {Number(inv.vat || 0) > 0 && <Field label="VAT" value={fmtMoney(inv.vat)} inline />}
        <div className="flex items-center justify-between border-t border-slate-100 pt-2">
          <span className="font-semibold text-slate-900">Total due</span>
          <span className="text-lg font-bold text-slate-900" data-testid="portal-invoice-total">{fmtMoney(inv.total)}</span>
        </div>
      </Card>
    </div>
  );
}

function Field({ label, value, inline }) {
  return (
    <div className={inline ? "flex items-center justify-between py-1 text-sm" : ""}>
      <p className="text-xs uppercase tracking-wide text-slate-400">{label}</p>
      <p className="font-medium text-slate-800">{value}</p>
    </div>
  );
}

function Back({ nav }) {
  return (
    <button onClick={() => nav("/portal/invoices")} className="mb-3 flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800">
      <ArrowLeft className="h-4 w-4" /> Back to invoices
    </button>
  );
}
